/**
 * Datasets 목록의 보기 방식(카드 그리드 · 표) 저장.
 *
 * 조회 축(검색 · 숨김 포함)과 달리 **표시 취향**이라 URL 이 아니라 브라우저가 기억한다 — 공유한
 * 링크가 남의 보기 방식을 강제하지 않는다(규칙 §13 은 조회 축만 URL 소유로 정한다).
 *
 * Owns:
 * - 저장 키 · 기본값 · 읽기/쓰기(저장소 불가 환경에서의 폴백).
 *
 * Does not own:
 * - 토글 UI — `@/shared/ui/ViewModeToggle`. 그리기 분기 — `page/components/DatasetListBody.tsx`.
 */
import { isViewMode, type ViewMode } from "@/shared/ui/ViewModeToggle";

export const DATASET_VIEW_MODE_STORAGE_KEY = "spawning-pool:datasets:view-mode";

/** Registry 와 같은 첫인상 — 카드 그리드가 기본이다. */
export const DEFAULT_DATASET_VIEW_MODE: ViewMode = "grid";

type ViewModeStorage = Pick<Storage, "getItem" | "setItem">;

function defaultStorage(): ViewModeStorage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}

/** 저장된 보기 방식. 없거나 모르는 값이거나 저장소를 못 읽으면 기본값이다. */
export function readDatasetViewMode(storage: ViewModeStorage | null = defaultStorage()): ViewMode {
  if (storage === null) {
    return DEFAULT_DATASET_VIEW_MODE;
  }
  try {
    const value = storage.getItem(DATASET_VIEW_MODE_STORAGE_KEY);
    return isViewMode(value) ? value : DEFAULT_DATASET_VIEW_MODE;
  } catch {
    return DEFAULT_DATASET_VIEW_MODE;
  }
}

/** 보기 방식을 저장한다. 저장소가 막혀 있어도(사생활 모드 등) 화면 전환은 막지 않는다. */
export function writeDatasetViewMode(
  mode: ViewMode,
  storage: ViewModeStorage | null = defaultStorage(),
): void {
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(DATASET_VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}
